import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ContactManagement from '../components/ContactManagement';
import CompanyDocuments from '../components/CompanyDocuments';
import CompanyDeployments from '../components/CompanyDeployments';
import CompanyComplianceConfig from '../components/CompanyComplianceConfig';
import SearchableSelect from '../components/SearchableSelect';
import '../CrmStyles.css';
import { 
  LuArrowLeft, 
  LuBuilding2, 
  LuMail, 
  LuPhone, 
  LuGlobe, 
  LuCreditCard, 
  LuBriefcase, 
  LuMapPin, 
  LuPencil, 
  LuCheck, 
  LuX, 
  LuCircleAlert,
  LuFileText,
  LuUsers,
  LuNetwork,
  LuPlus,
  LuTrash2,
  LuShieldCheck
} from 'react-icons/lu';
import { countriesData, getStatesForCountry } from '../../erprecruitment/config/locationConfig';

import BASE_URL from '../../url';

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusSuccess, setStatusSuccess] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({});

  // MSP Relationship state
  const [showMspModal, setShowMspModal] = useState(false);
  const [mspModalType, setMspModalType] = useState('ADD_MSP'); // 'ADD_MSP' or 'ADD_CLIENT'
  const [targetCompanyOptions, setTargetCompanyOptions] = useState([]);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [relationshipNotes, setRelationshipNotes] = useState('');
  const [savingRelationship, setSavingRelationship] = useState(false);
  const [relationshipError, setRelationshipError] = useState('');

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/crm/companies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setData(response.data.data);
        // Coerce SQL bit (0/1) to boolean
        setIsActive(!!response.data.data.company.IsActive);
        setIsApproved(!!response.data.data.company.IsApproved);
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  const handleToggleStatus = async (newActive, newApproved) => {
    setIsActive(newActive);
    setIsApproved(newApproved);
    setStatusUpdating(true);
    setStatusError('');
    setStatusSuccess('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BASE_URL}/api/crm/companies/${id}/status`, {
        isActive: newActive ? 1 : 0,
        isApproved: newApproved ? 1 : 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStatusSuccess('Status updated successfully!');
        setData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            company: {
              ...prev.company,
              IsActive: newActive ? 1 : 0,
              IsApproved: newApproved ? 1 : 0
            }
          };
        });
        setTimeout(() => setStatusSuccess(''), 3000);
      } else {
        setStatusError(response.data.message || 'Failed to update status');
        setIsActive(!!data?.company?.IsActive);
        setIsApproved(!!data?.company?.IsApproved);
      }
    } catch (error) {
      setStatusError(error.response?.data?.message || 'Error updating status');
      setIsActive(!!data?.company?.IsActive);
      setIsApproved(!!data?.company?.IsApproved);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleStatusUpdate = async () => {
    handleToggleStatus(isActive, isApproved);
  };

  const handleEditClick = () => {
    setActiveTab('overview');
    const { company } = data;
    setFormData({
      companyName: company.CompanyName || '',
      displayName: company.DisplayName || '',
      isClient: company.IsClient || false,
      isSupplier: company.IsSupplier || false,
      isMSP: company.IsMSP || false,
      isActive: company.IsActive !== undefined ? !!company.IsActive : true,
      isApproved: company.IsApproved !== undefined ? !!company.IsApproved : true,
      industryCode: company.IndustryCode || '',
      email: company.Email || '',
      phone: company.Phone || '',
      website: company.Website || '',
      taxId: company.TaxId || '',
      addressLine1: company.AddressLine1 || '',
      addressLine2: company.AddressLine2 || '',
      city: company.City || '',
      state: company.State || '',
      postalCode: company.PostalCode || '',
      country: company.Country || '',
      isIndividual: (!company.CompanyName && company.FirstName) ? true : false,
      firstName: company.FirstName || '',
      lastName: company.LastName || '',
    });
    setIsEditMode(true);
  };

  const generateDisplayName = (baseName, countryCode) => {
    const raw = (baseName || '').trim();
    if (!raw) return '';
    const cleanBase = raw.replace(/\s*\([A-Za-z]{2,3}\)$/g, '').trim();
    if (countryCode && countryCode.trim()) {
      return `${cleanBase} (${countryCode.trim().toUpperCase()})`;
    }
    return cleanBase;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setFormData(prev => {
      const base = (prev.displayName && prev.displayName.trim() !== '') ? prev.displayName : prev.companyName;
      return {
        ...prev,
        country: selectedCountry,
        state: '',
        displayName: generateDisplayName(base, selectedCountry)
      };
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BASE_URL}/api/crm/companies/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setIsEditMode(false);
        if (formData.isActive !== undefined) setIsActive(!!formData.isActive);
        if (formData.isApproved !== undefined) setIsApproved(!!formData.isApproved);
        fetchDetails();
      } else {
        alert(response.data.message || 'Failed to update company.');
      }
    } catch (error) {
      console.error('Error saving company:', error);
      alert(error.response?.data?.message || 'Error updating company details.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenAddMsp = async () => {
    setMspModalType('ADD_MSP');
    setSelectedTargetId('');
    setRelationshipNotes('');
    setRelationshipError('');
    setShowMspModal(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/companies?role=MSP`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        // Exclude current company
        setTargetCompanyOptions(res.data.data.filter(c => String(c.CompanyId) !== String(id)));
      }
    } catch (e) {
      console.error('Error fetching MSP companies:', e);
    }
  };

  const handleOpenAddClient = async () => {
    setMspModalType('ADD_CLIENT');
    setSelectedTargetId('');
    setRelationshipNotes('');
    setRelationshipError('');
    setShowMspModal(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/companies?role=Client`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        // Exclude current company
        setTargetCompanyOptions(res.data.data.filter(c => String(c.CompanyId) !== String(id)));
      }
    } catch (e) {
      console.error('Error fetching client companies:', e);
    }
  };

  const handleSaveRelationship = async () => {
    if (!selectedTargetId) {
      setRelationshipError(`Please select a ${mspModalType === 'ADD_MSP' ? 'Managed Service Provider' : 'Client'}.`);
      return;
    }
    setSavingRelationship(true);
    setRelationshipError('');
    try {
      const token = localStorage.getItem('token');
      const clientId = mspModalType === 'ADD_MSP' ? id : selectedTargetId;
      const mspId = mspModalType === 'ADD_MSP' ? selectedTargetId : id;

      await axios.post(`${BASE_URL}/api/crm/companies/${clientId}/msps`, {
        mspCompanyId: mspId,
        notes: relationshipNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowMspModal(false);
      await fetchDetails();
    } catch (e) {
      setRelationshipError(e.response?.data?.message || 'Error saving relationship');
    } finally {
      setSavingRelationship(false);
    }
  };

  const handleRemoveRelationship = async (companyMspId, name) => {
    if (!window.confirm(`Are you sure you want to remove the MSP relationship with ${name}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/crm/companies/${id}/msps/${companyMspId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDetails();
    } catch (e) {
      alert(e.response?.data?.message || 'Error removing MSP relationship');
    }
  };

  if (loading) return <p style={{ padding: '20px' }}>Loading...</p>;
  if (!data) return <p style={{ padding: '20px' }}>Company not found.</p>;

  const { company, contacts } = data;

  return (
    <div className="crm-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <button onClick={() => navigate('/crm/companies')} className="crm-back-link" style={{ background: 'none', border: 'none', padding: 0, margin: 0 }}>
          <LuArrowLeft size={16} /> Back to Dashboard
        </button>
        {!isEditMode ? (
          <button onClick={handleEditClick} className="crm-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px' }}>
            <LuPencil size={16} /> Edit Company
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setIsEditMode(false)} className="crm-btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px' }} disabled={isSaving}>
              <LuX size={16} /> Cancel
            </button>
            <button onClick={handleSave} className="crm-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px' }} disabled={isSaving}>
              <LuCheck size={16} /> {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
      
      {!isApproved && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <LuCircleAlert size={20} style={{ flexShrink: 0 }} />
          <span><strong>Notice:</strong> This company is <strong>PENDING</strong> approval and cannot be used in placement chains, job submissions, or deployments.</span>
        </div>
      )}
      
      {/* Company Summary Top Card */}
      <div className="crm-card" style={{ marginBottom: '16px' }}>
        <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: 'none' }}>
          <div>
            {!isEditMode ? (
              <>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#111827' }}>
                  <LuBuilding2 size={24} style={{ verticalAlign: 'middle', marginRight: '8px', color: '#3b5bfd' }} />
                  {company.DisplayName || company.CompanyName}
                </h2>
                {company.DisplayName && <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '14px' }}>Legal Name: {company.CompanyName}</p>}
              </>
            ) : (
              <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
                <div className="crm-form-group" style={{ marginBottom: 0 }}>
                  <label>Display Name</label>
                  <input type="text" name="displayName" className="crm-input" value={formData.displayName} onChange={handleChange} />
                </div>
                <div className="crm-form-group" style={{ marginBottom: 0 }}>
                  <label>Legal Company Name</label>
                  <input type="text" name="companyName" className="crm-input" value={formData.companyName} onChange={handleChange} />
                </div>
              </div>
            )}
            <p style={{ margin: 0, color: '#6b7280', fontFamily: 'monospace', fontSize: '13px', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', display: 'inline-block' }}>
              {company.CompanyCode}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Status Management inline controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: statusUpdating ? 'wait' : 'pointer', fontSize: '13px' }}>
                <input 
                  type="checkbox" 
                  checked={isActive} 
                  disabled={statusUpdating}
                  onChange={(e) => handleToggleStatus(e.target.checked, isApproved)} 
                  style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }}
                />
                <span style={{ fontWeight: '500', color: '#4b5563' }}>Is Active</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: statusUpdating ? 'wait' : 'pointer', fontSize: '13px' }}>
                <input 
                  type="checkbox" 
                  checked={isApproved} 
                  disabled={statusUpdating}
                  onChange={(e) => handleToggleStatus(isActive, e.target.checked)} 
                  style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }}
                />
                <span style={{ fontWeight: '500', color: '#4b5563' }}>Is Approved</span>
              </label>
              {statusUpdating && (
                <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>Saving...</span>
              )}
              {statusSuccess && !statusUpdating && (
                <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>Saved ✓</span>
              )}
            </div>

            <div style={{ width: '1px', height: '24px', background: '#eaeaea' }}></div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span className="crm-badge crm-badge-role">
                {(() => {
                  const roles = [];
                  if (company.IsClient) roles.push('Client');
                  if (company.IsSupplier) roles.push('Supplier');
                  if (company.IsMSP) roles.push('MSP');
                  return roles.length > 0 ? roles.join(' & ') : 'Unknown';
                })()}
              </span>
              <span className={`crm-badge crm-badge-${isActive ? (isApproved ? 'active' : 'pending') : 'inactive'}`}>
                {isActive ? (isApproved ? 'ACTIVE' : 'PENDING') : 'INACTIVE'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 3 Tabs Navigation */}
      <div className="crm-tabs-container">
        <button
          type="button"
          className={`crm-tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <LuBuilding2 size={16} />
          Overview
        </button>

        <button
          type="button"
          className={`crm-tab-button ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <LuFileText size={16} />
          Documents &amp; Agreements
        </button>

        <button
          type="button"
          className={`crm-tab-button ${activeTab === 'deployments' ? 'active' : ''}`}
          onClick={() => setActiveTab('deployments')}
        >
          <LuUsers size={16} />
          Deployments
        </button>


        <button
          type="button"
          className={`crm-tab-button ${activeTab === 'compliance' ? 'active' : ''}`}
          onClick={() => setActiveTab('compliance')}
        >
          <LuShieldCheck size={16} />
          Compliance Config
        </button>
      </div>

      {/* Tab 1: Overview */}
      {activeTab === 'overview' && (
        <>
          <div className="crm-card" style={{ marginBottom: '16px' }}>
            <div className="crm-card-body">
              {statusError && <div style={{ color: '#ef4444', marginBottom: '16px', fontSize: '14px', background: '#fef2f2', padding: '10px', borderRadius: '6px', border: '1px solid #fecaca' }}>{statusError}</div>}
              {statusSuccess && <div style={{ color: '#065f46', marginBottom: '16px', fontSize: '14px', background: '#ecfdf5', padding: '10px', borderRadius: '6px', border: '1px solid #6ee7b7' }}>{statusSuccess}</div>}
              <div className="crm-details-grid">
                <div className="crm-detail-item">
                  <span className="crm-detail-label">Email</span>
                  {!isEditMode ? (
                    <span className="crm-detail-value">
                      <LuMail size={16} color="#9ca3af" /> {company.Email || 'N/A'}
                    </span>
                  ) : (
                    <input type="email" name="email" className="crm-input" value={formData.email} onChange={handleChange} />
                  )}
                </div>
                <div className="crm-detail-item">
                  <span className="crm-detail-label">Phone</span>
                  {!isEditMode ? (
                    <span className="crm-detail-value">
                      <LuPhone size={16} color="#9ca3af" /> {company.Phone || 'N/A'}
                    </span>
                  ) : (
                    <input type="text" name="phone" className="crm-input" value={formData.phone} onChange={handleChange} />
                  )}
                </div>
                <div className="crm-detail-item">
                  <span className="crm-detail-label">Website</span>
                  {!isEditMode ? (
                    <span className="crm-detail-value">
                      <LuGlobe size={16} color="#9ca3af" style={{ flexShrink: 0 }} /> 
                      {company.Website ? (
                        <a 
                          href={company.Website.startsWith('http') ? company.Website : `https://${company.Website}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ color: '#3b5bfd', textDecoration: 'none' }}
                          onMouseOver={(e) => e.target.style.textDecoration='underline'} 
                          onMouseOut={(e) => e.target.style.textDecoration='none'}
                        >
                          Link
                        </a>
                      ) : 'N/A'}
                    </span>
                  ) : (
                    <input type="text" name="website" className="crm-input" value={formData.website} onChange={handleChange} />
                  )}
                </div>
                <div className="crm-detail-item">
                  <span className="crm-detail-label">Industry</span>
                  {!isEditMode ? (
                    <span className="crm-detail-value">
                      <LuBriefcase size={16} color="#9ca3af" /> {company.IndustryCode || 'N/A'}
                    </span>
                  ) : (
                    <select name="industryCode" className="crm-select" value={formData.industryCode} onChange={handleChange}>
                      <option value="">Select Industry</option>
                      <option value="TECHNOLOGY">Technology</option>
                      <option value="HEALTHCARE">Healthcare</option>
                      <option value="FINANCE">Finance</option>
                      <option value="OTHER">Other</option>
                    </select>
                  )}
                </div>
                <div className="crm-detail-item">
                  <span className="crm-detail-label">Tax ID</span>
                  {!isEditMode ? (
                    <span className="crm-detail-value">
                      <LuCreditCard size={16} color="#9ca3af" /> {company.TaxId ? '***' + company.TaxId.slice(-4) : 'N/A'}
                    </span>
                  ) : (
                    <input type="text" name="taxId" className="crm-input" value={formData.taxId} onChange={handleChange} />
                  )}
                </div>

                {!isEditMode ? (
                  <div className="crm-detail-item" style={{ gridColumn: 'span 2' }}>
                    <span className="crm-detail-label">Address</span>
                    <span className="crm-detail-value" style={{ wordBreak: 'break-word', alignItems: 'flex-start' }}>
                      <LuMapPin size={16} color="#9ca3af" style={{ flexShrink: 0, marginTop: '3px' }} /> 
                      <span style={{ display: 'inline-block' }}>
                        {company.AddressLine1 
                          ? `${company.AddressLine1}${company.AddressLine2 ? ` ${company.AddressLine2}` : ''}, ${company.City || ''}, ${company.State || ''} ${company.PostalCode || ''} ${company.Country || ''}`
                          : 'N/A'}
                      </span>
                    </span>
                  </div>
                ) : (
                  <div style={{ gridColumn: '1 / -1', marginTop: '16px' }}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>Address Details</h4>
                    <div className="crm-grid-2">
                      <div className="crm-form-group">
                        <label>Street Address</label>
                        <input type="text" name="addressLine1" className="crm-input" value={formData.addressLine1} onChange={handleChange} />
                      </div>
                      <div className="crm-form-group">
                        <label>Apartment, suite, etc.</label>
                        <input type="text" name="addressLine2" className="crm-input" value={formData.addressLine2} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="crm-grid-2">
                      <div className="crm-form-group">
                        <label>Country</label>
                        <select 
                          name="country" 
                          className="crm-select" 
                          value={formData.country} 
                          onChange={handleCountryChange}
                        >
                          <option value="">Select Country</option>
                          {countriesData.map(c => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="crm-form-group">
                        <label>State / Province</label>
                        <select name="state" className="crm-select" value={formData.state} onChange={handleChange} disabled={!formData.country}>
                          <option value="">Select State</option>
                          {formData.country && getStatesForCountry(formData.country).map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="crm-grid-2">
                      <div className="crm-form-group">
                        <label>City</label>
                        <input type="text" name="city" className="crm-input" value={formData.city} onChange={handleChange} />
                      </div>
                      <div className="crm-form-group">
                        <label>Postal Code</label>
                        <input type="text" name="postalCode" className="crm-input" value={formData.postalCode} onChange={handleChange} />
                      </div>
                    </div>

                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151' }}>Status Management</h4>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                          <input 
                            type="checkbox" 
                            name="isActive" 
                            checked={!!formData.isActive} 
                            onChange={handleChange} 
                            style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }}
                          />
                          <span style={{ fontWeight: 500, color: '#374151' }}>Is Active</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                          <input 
                            type="checkbox" 
                            name="isApproved" 
                            checked={!!formData.isApproved} 
                            onChange={handleChange} 
                            style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }}
                          />
                          <span style={{ fontWeight: 500, color: '#374151' }}>Is Approved</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <ContactManagement companyId={id} contacts={contacts} onRefresh={fetchDetails} isEditMode={isEditMode} />

          {/* MSP Relationships for Clients */}
          {company.IsClient && (
            <div className="crm-card" style={{ marginTop: '16px' }}>
              <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LuNetwork size={18} style={{ color: '#3b5bfd' }} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>MSP Relationships</h3>
                  <span style={{ fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '12px' }}>
                    {data.msps?.length || 0}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleOpenAddMsp}
                  className="crm-btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 12px' }}
                >
                  <LuPlus size={16} /> Add MSP
                </button>
              </div>
              <div className="crm-card-body" style={{ padding: 0 }}>
                {data.msps && data.msps.length > 0 ? (
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th>MSP Company</th>
                        <th>MSP Code</th>
                        <th>Relationship Notes</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.msps.map((m) => (
                        <tr key={m.CompanyMSPId}>
                          <td style={{ fontWeight: 600, color: '#111827' }}>
                            <span 
                              style={{ cursor: 'pointer', color: '#3b5bfd' }}
                              onClick={() => navigate(`/crm/companies/${m.MSPCompanyId}`)}
                            >
                              {m.MSPDisplayName || m.MSPCompanyName}
                            </span>
                          </td>
                          <td style={{ color: '#4b5563', fontFamily: 'monospace' }}>{m.MSPCompanyCode}</td>
                          <td style={{ color: '#6b7280' }}>{m.Notes || '—'}</td>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleRemoveRelationship(m.CompanyMSPId, m.MSPDisplayName || m.MSPCompanyName)}
                              className="crm-btn-danger"
                              style={{ padding: '4px 8px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              title="Deactivate MSP link"
                            >
                              <LuTrash2 size={13} /> Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                    No MSP relationships linked to this client yet. Click &quot;Add MSP&quot; to link one.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Managed Clients for MSPs */}
          {company.IsMSP && (
            <div className="crm-card" style={{ marginTop: '16px' }}>
              <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LuNetwork size={18} style={{ color: '#3b5bfd' }} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Clients Managed</h3>
                  <span style={{ fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '12px' }}>
                    {data.managedClients?.length || 0}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleOpenAddClient}
                  className="crm-btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 12px' }}
                >
                  <LuPlus size={16} /> Add Client
                </button>
              </div>
              <div className="crm-card-body" style={{ padding: 0 }}>
                {data.managedClients && data.managedClients.length > 0 ? (
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th>Client Company</th>
                        <th>Client Code</th>
                        <th>Notes / Program Details</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.managedClients.map((c) => (
                        <tr key={c.CompanyMSPId}>
                          <td style={{ fontWeight: 600, color: '#111827' }}>
                            <span 
                              style={{ cursor: 'pointer', color: '#3b5bfd' }}
                              onClick={() => navigate(`/crm/companies/${c.ClientCompanyId}`)}
                            >
                              {c.ClientDisplayName || c.ClientCompanyName}
                            </span>
                          </td>
                          <td style={{ color: '#4b5563', fontFamily: 'monospace' }}>{c.ClientCompanyCode}</td>
                          <td style={{ color: '#6b7280' }}>{c.Notes || '—'}</td>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleRemoveRelationship(c.CompanyMSPId, c.ClientDisplayName || c.ClientCompanyName)}
                              className="crm-btn-danger"
                              style={{ padding: '4px 8px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              title="Deactivate client link"
                            >
                              <LuTrash2 size={13} /> Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                    No clients linked to this MSP yet. Click &quot;Add Client&quot; to link one.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modal to Add MSP or Client */}
          {showMspModal && (
            <div className="crm-modal-overlay" style={{ zIndex: 1000 }}>
              <div 
                className="crm-modal-content" 
                style={{ 
                  maxWidth: '480px', 
                  width: '90%', 
                  background: '#ffffff', 
                  borderRadius: '12px', 
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08)', 
                  overflow: 'visible',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div className="crm-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                    {mspModalType === 'ADD_MSP' ? 'Link MSP to Client' : 'Link Client to MSP'}
                  </h3>
                  <button type="button" onClick={() => setShowMspModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                    <LuX size={18} />
                  </button>
                </div>
                <div className="crm-modal-body" style={{ padding: '20px', background: '#ffffff', overflow: 'visible' }}>
                  {relationshipError && (
                    <div style={{ color: '#ef4444', background: '#fef2f2', padding: '8px 12px', borderRadius: '6px', marginBottom: '14px', fontSize: '13px', border: '1px solid #fecaca' }}>
                      {relationshipError}
                    </div>
                  )}
                  <div className="crm-form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px', color: '#374151' }}>
                      {mspModalType === 'ADD_MSP' ? 'Select MSP Company *' : 'Select Client Company *'}
                    </label>
                    <SearchableSelect
                      options={targetCompanyOptions.map(tc => ({
                        value: tc.CompanyId,
                        label: `${tc.DisplayName || tc.CompanyName} (${tc.CompanyCode})`,
                        subLabel: tc.CompanyCode
                      }))}
                      value={selectedTargetId}
                      onChange={(val) => setSelectedTargetId(val)}
                      placeholder={`-- Choose ${mspModalType === 'ADD_MSP' ? 'MSP' : 'Client'} --`}
                      searchPlaceholder={`Search ${mspModalType === 'ADD_MSP' ? 'MSP' : 'client'}...`}
                      allowClear={true}
                    />
                  </div>
                  <div className="crm-form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px', color: '#374151' }}>
                      Relationship Notes (Optional)
                    </label>
                    <textarea
                      className="crm-input"
                      rows={3}
                      placeholder={mspModalType === 'ADD_MSP' ? 'e.g., Manages US contingent placements' : 'e.g., Program scope or division'}
                      value={relationshipNotes}
                      onChange={(e) => setRelationshipNotes(e.target.value)}
                      style={{ width: '100%', resize: 'vertical', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
                    />
                  </div>
                </div>
                <div className="crm-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', background: '#f8fafc', borderTop: '1px solid #e5e7eb' }}>
                  <button type="button" onClick={() => setShowMspModal(false)} className="crm-btn-secondary" disabled={savingRelationship} style={{ padding: '6px 14px', fontSize: '13px' }}>
                    Cancel
                  </button>
                  <button type="button" onClick={handleSaveRelationship} className="crm-btn-primary" disabled={savingRelationship} style={{ padding: '6px 16px', fontSize: '13px' }}>
                    {savingRelationship ? 'Saving...' : 'Link Relationship'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Tab 2: Documents & Agreements */}
      {activeTab === 'documents' && (
        <CompanyDocuments companyId={id} company={company} isEditMode={isEditMode} />
      )}

      {/* Tab 3: Deployments */}
      {activeTab === 'deployments' && (
        <CompanyDeployments companyId={id} />
      )}


      {/* Tab 5: Compliance Config */}
      {activeTab === 'compliance' && (
        <CompanyComplianceConfig companyId={id} company={company} />
      )}
    </div>
  );
};

export default CompanyDetails;
